import { AxiosError, AxiosHeaders, type AxiosAdapter, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { commit, type SandboxStore } from './store';

/**
 * The axios adapter that answers `/api/v3` calls from the sandbox store
 * instead of the network. Routes are matched in order; a GET nobody claims
 * goes to the real API (identity, notifications, avatars stay real), and
 * so do a few harmless writes; any other write answers 501 so the app
 * shows "Not available in the sandbox" rather than changing real data.
 */

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RouteContext {
  readonly store: SandboxStore;
  readonly params: Readonly<Record<string, string>>;
  readonly query: URLSearchParams;
  readonly body: unknown;
}

export interface RouteResult {
  readonly status?: number;
  readonly data?: unknown;
  readonly pagination?: unknown;
  readonly meta?: unknown;
}

export interface Route {
  readonly method: Method;
  readonly pattern: RegExp;
  readonly keys: readonly string[];
  readonly handle: (context: RouteContext) => RouteResult;
}

/** Thrown by a handler to answer with an error the app already knows how to show. */
export class SandboxError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
  }
}

const API_PREFIX = '/api/v3';

/** Writes that are safe to let through to the real API from the sandbox. */
const PASS_THROUGH_WRITES = [/^\/telemetry\//, /^\/users\/me(\/|$)/, /^\/feedback(\/|$)/, /^\/notifications(\/|$)/, /^\/media$/];

export function route(method: Method, path: string, handle: Route['handle']): Route {
  const keys: string[] = [];
  const source = path
    .split('/')
    .map((segment) => {
      if (segment.startsWith(':')) {
        keys.push(segment.slice(1));
        return '([^/]+)';
      }
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  return { method, pattern: new RegExp(`^${source}$`), keys, handle };
}

function requestPath(config: InternalAxiosRequestConfig): { path: string; query: URLSearchParams } | null {
  const raw = config.url ?? '';
  const joined = raw.startsWith('/api/') || !config.baseURL ? raw : `${config.baseURL}${raw.startsWith('/') ? '' : '/'}${raw}`;
  if (!joined.startsWith(API_PREFIX)) return null;
  const [pathPart = '', queryPart = ''] = joined.slice(API_PREFIX.length).split('?');
  const query = new URLSearchParams(queryPart);
  const params = config.params as Record<string, unknown> | undefined;
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;
      query.set(key, String(value));
    }
  }
  return { path: pathPart.replace(/\/+$/, '') || '/', query };
}

function parseBody(config: InternalAxiosRequestConfig): unknown {
  const data: unknown = config.data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}

function respond(config: InternalAxiosRequestConfig, status: number, body: unknown): AxiosResponse {
  return { data: body, status, statusText: status === 200 ? 'OK' : status === 201 ? 'Created' : 'Error', headers: {}, config, request: {} };
}

function fail(config: InternalAxiosRequestConfig, status: number, message: string, code?: string): AxiosError {
  const response = respond(config, status, { success: false, error: message, ...(code ? { code } : {}) });
  return new AxiosError(message, code ?? 'ERR_BAD_REQUEST', config, {}, response);
}

export function createSandboxAdapter(real: AxiosAdapter, routes: readonly Route[], store: () => SandboxStore): AxiosAdapter {
  return async (config) => {
    const method = (config.method ?? 'get').toUpperCase() as Method;
    const target = requestPath(config);
    if (!target) return real(config);
    const { path, query } = target;
    if (path === '/media' && method === 'POST') return respond(config, 201, { success: true, data: mediaFromForm(config) });

    for (const candidate of routes) {
      if (candidate.method !== method) continue;
      const match = candidate.pattern.exec(path);
      if (!match) continue;
      const params: Record<string, string> = {};
      candidate.keys.forEach((key, index) => {
        params[key] = decodeURIComponent(match[index + 1] ?? '');
      });
      const current = store();
      try {
        const result = candidate.handle({ store: current, params, query, body: parseBody(config) });
        if (method !== 'GET') commit(current);
        const envelope: Record<string, unknown> = { success: true, data: result.data ?? null };
        if (result.pagination !== undefined) envelope['pagination'] = result.pagination;
        if (result.meta !== undefined) envelope['meta'] = result.meta;
        return respond(config, result.status ?? (method === 'POST' ? 201 : 200), envelope);
      } catch (error) {
        if (error instanceof SandboxError) throw fail(config, error.status, error.message, error.code);
        throw error;
      }
    }

    if (method === 'GET' || PASS_THROUGH_WRITES.some((pattern) => pattern.test(path))) return real(config);
    throw fail(config, 501, 'Not available in the sandbox.', 'SANDBOX_READ_ONLY');
  };
}

function mediaFromForm(config: InternalAxiosRequestConfig): unknown {
  const form = config.data;
  const file = form instanceof FormData ? form.get('file') : null;
  const name = file instanceof File ? file.name : 'upload';
  const type = file instanceof File ? file.type : 'application/octet-stream';
  const size = file instanceof File ? file.size : 0;
  const url = file instanceof File ? URL.createObjectURL(file) : '';
  return { id: `sandbox-${Date.now()}`, url, provider: 'sandbox', location: name, contentType: type, size, kind: form instanceof FormData ? (form.get('kind') as string | null) : null, organizationId: null };
}

export { AxiosHeaders };
